import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion from "@insite/mobius/Accordion";
import AccordionSection, { AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Tab from "@insite/mobius/Tab";
import TabGroup, { TabGroupPresentationProps } from "@insite/mobius/TabGroup";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import MinusIcon from "@tSurya/Icons/MinusIcon";
import PlusIcon from "@tSurya/Icons/PlusIcon";
import CustomApplicationState from "@tSurya/Store/Data/CustomApplicationState/CustomApplicationState";
import { COLORS, TYPOGRAPHY } from "@tSurya/theme/design";
import parse from "html-react-parser";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    numberOfTabs = "numberOfTabs",
    faqBoxes = "faqBoxes",
    cssClass = "cssClass",
}

interface NameArray {
    name: string;
    faqBoxes: FAQModel[];
    tabId: string;
}

interface TabModel {
    fields: NameArray;
}

interface FAQArray {
    questions: string;
    answers: string;
}

interface FAQModel {
    fields: FAQArray;
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.numberOfTabs]: TabModel[];
        [fields.faqBoxes]: FAQModel;
        [fields.cssClass]: string;
    };
}

const mapStateToProps = (state: CustomApplicationState) => {
    return {
        headerHeight: state.SURData.headerHeightStore.headerHeight,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface SurTabularDataStyles {
    tabView?: InjectableCss;
    tabTitle?: InjectableCss;
    tabGroup?: TabGroupPresentationProps;
    accordianContainer?: InjectableCss;
    accordianSection?: AccordionSectionPresentationProps;
    container?: InjectableCss;
}
export const tabularDataStyles: SurTabularDataStyles = {
    tabView: {
        css: css`
            background: ${getColor("secondary")};
            padding: 8px 12px;
            font-family: ${TYPOGRAPHY.fontFamily.base};
            font-style: normal;
            min-width: auto;
            outline: 0;
            font-weight: ${TYPOGRAPHY.weight.base};
            font-size: ${TYPOGRAPHY.size.small};
            line-height: ${TYPOGRAPHY.lineHeight.msmall};
            cursor: pointer;
            transition: none !important;
            :focus {
                outline: 0;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 12px;
                    `,
                    css`
                        padding: 12px;
                    `,
                    css`
                        padding: 12px;
                    `,
                    null,
                    null,
                ])}
            &[aria-selected="false"] {
                border-bottom: 2px solid transparent;
            }
            &[aria-selected="true"] {
                border-bottom: 2px solid ${getColor("text.main")};
                span {
                    font-family: ${TYPOGRAPHY.fontFamily.base};
                    font-style: normal;
                    font-weight: 700;
                    font-size: 15px;
                    line-height: 24px;
                    color: ${getColor("text.main")};
                }
            }
            /* &:first-child {
                color: ${getColor("text.main")};
            } */
        `,
    },
    tabTitle: {
        css: css`
            font-family: ${TYPOGRAPHY.fontFamily.base};
            font-style: normal;
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            font-size: 28px;
            line-height: 36px;
            color: ${getColor("text.main")};
            /* identical to box height, or 117% */
            /* margin-bottom: 24px; */
            text-align: center;
            text-transform: uppercase;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-weight: ${TYPOGRAPHY.weight.mediumbold};
                        color: ${COLORS.featuredMobile};
                        font-size: ${TYPOGRAPHY.size.largest};
                        line-height: ${TYPOGRAPHY.lineHeight.larger};
                    `,
                    css`
                        font-weight: ${TYPOGRAPHY.weight.mediumbold};
                        color: ${COLORS.featuredMobile};
                        font-size: ${TYPOGRAPHY.size.largest};
                        line-height: ${TYPOGRAPHY.lineHeight.larger};
                    `,
                    css`
                        font-weight: ${TYPOGRAPHY.weight.mediumbold};
                        color: ${COLORS.featuredMobile};
                        font-size: ${TYPOGRAPHY.size.largest};
                        line-height: ${TYPOGRAPHY.lineHeight.larger};
                    `,
                    null,
                    null,
                ])}
        `,
    },
    tabGroup: {
        cssOverrides: {
            tabGroup: css`
                align-items: center;
                justify-content: flex-start;
                margin: 0px auto 22px auto;
                gap: 20px;
                z-index: 9;
                &::-webkit-scrollbar{
                    width:0;
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            justify-content: flex-start;
                            padding: 0 20px;
                        `,
                        css`
                            justify-content: flex-start;
                            padding: 0 20px;
                        `,
                        css`
                            padding: 0 20px;
                        `,
                        null,
                        null,
                    ])}
                > li > span {
                    font-family: ${TYPOGRAPHY.fontFamily.title};
                    font-weight: ${TYPOGRAPHY.weight.base};
                    color: ${getColor("text.main")};
                    font-size: ${TYPOGRAPHY.size.small};
                    line-height: ${TYPOGRAPHY.lineHeight.xbase};
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            css`
                                font-family: ${TYPOGRAPHY.fontFamily.title};
                                font-weight: ${TYPOGRAPHY.weight.base};
                                color: ${getColor("text.main")};
                                font-size: ${TYPOGRAPHY.size.base};
                                line-height: ${TYPOGRAPHY.lineHeight.msmall};
                            `,
                            css`
                                font-family: ${TYPOGRAPHY.fontFamily.title};
                                font-weight: ${TYPOGRAPHY.weight.base};
                                color: ${getColor("text.main")};
                                font-size: ${TYPOGRAPHY.size.base};
                                line-height: ${TYPOGRAPHY.lineHeight.msmall};
                            `,
                            css`
                                font-family: ${TYPOGRAPHY.fontFamily.title};
                                font-weight: ${TYPOGRAPHY.weight.base};
                                color: ${getColor("text.main")};
                                font-size: ${TYPOGRAPHY.size.base};
                                line-height: ${TYPOGRAPHY.lineHeight.msmall};
                            `,
                            null,
                            null,
                        ])}
                }
            `,
            tabContent: css`
                border: none;
                padding: 0px !important;
            `,
        },
    },
    accordianContainer: {
        css: css`
            border-bottom: 2px solid ${getColor("common.backgroundContrast")}; ;
        `,
    },
    accordianSection: {
        headerProps: {
            css: css`
                & button {
                    background-color: ${getColor("primary.contrast")};
                    padding: 24px 20px;
                    border: 0 !important;
                }
                & button:focus {
                    background-color: ${getColor("primary.contrast")};
                    padding: 24px 20px;
                    border: 0;
                }
                span {
                    font-family: ${TYPOGRAPHY.fontFamily.base};
                    font-style: normal;
                    font-weight: 700;
                    font-size: 14px;
                    text-transform: none;
                    line-height: 22px;
                    color: ${getColor("text.main")};
                }
            `,
        },
        panelProps: {
            css: css`
                padding: 0px 20px 15px;
                border: 0;
                font-family: ${TYPOGRAPHY.fontFamily.base};
                p, span, li, ul, ol, strong, em, a {
                    font-family: inherit !important;
                }
            `,
        },
        expandIconProps: {
            src: PlusIcon,
        },
        collapseIconProps: {
            src: MinusIcon,
        },
    },
    container: {
        css: css`
            max-width: 955px;
            margin: 0 auto;
            padding-bottom: 70px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding-bottom: 35px;
                    `,
                    css`
                        padding-bottom: 35px;
                    `,
                    css`
                        padding-bottom: 35px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
};

const styles = tabularDataStyles;

const getTabAnchor = (tab: TabModel) => {
    const tabIdentifier = tab.fields.tabId || tab.fields.name;

    return tabIdentifier
        .replace(/^#/, "")
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");
};

const getRequestedTabAnchor = () => {
    const hashAnchor = window.location.hash.replace(/^#/, "");
    const queryAnchor = new URLSearchParams(window.location.search).get("tabId") || "";

    return getTabAnchor({ fields: { name: hashAnchor || queryAnchor, tabId: "", faqBoxes: [] } });
};

const SurTabAccordian: React.FC<Props> = ({ fields, id, headerHeight }) => {
    const [expandedAccordian, setExpandedAccordian] = React.useState<number | null>(null);
    const [defaultTabState, setDefaultTabState] = React.useState("All");
    const allContent: TabModel[] = [
        {
            fields: {
                name: "All",
                faqBoxes: fields.numberOfTabs
                    .map(item => item.fields.faqBoxes)
                    .reduce((acc, el) => acc.concat(el), []),
                tabId: "all",
            },
        },
        ...fields.numberOfTabs,
    ];

    React.useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }

        const selectRequestedTab = () => {
            const requestedTabAnchor = getRequestedTabAnchor();
            const matchingTab =
                allContent.find(tab => getTabAnchor(tab) === requestedTabAnchor) ||
                allContent.find(tab => getTabAnchor(tab) === "all");

            if (matchingTab) {
                setDefaultTabState(matchingTab.fields.name);
                if (requestedTabAnchor) {
                    window.requestAnimationFrame(() => {
                        const selectedTab = document.getElementById(requestedTabAnchor);
                        if (selectedTab) {
                            window.scrollTo({
                                top: selectedTab.offsetTop - (headerHeight || 0),
                            });
                        }
                    });
                }
            }
        };

        selectRequestedTab();
        window.addEventListener("hashchange", selectRequestedTab);
        window.addEventListener("popstate", selectRequestedTab);

        return () => {
            window.removeEventListener("hashchange", selectRequestedTab);
            window.removeEventListener("popstate", selectRequestedTab);
        };
    }, [fields.numberOfTabs, headerHeight]);

    const handleTabChange = (_event: React.MouseEvent | React.KeyboardEvent, tabKey?: string) => {
        const selectedTab = allContent.find(tab => tab.fields.name === tabKey);
        if (!selectedTab || typeof window === "undefined") {
            return;
        }

        const tabAnchor = getTabAnchor(selectedTab);
        const searchParameters = new URLSearchParams(window.location.search);
        searchParameters.delete("tabId");
        const search = searchParameters.toString();

        setDefaultTabState(selectedTab.fields.name);
        setExpandedAccordian(null);
        window.history.pushState(null, "", `${window.location.pathname}${search ? `?${search}` : ""}#${tabAnchor}`);
    };

    return (
        <StyledWrapper {...styles.container}>
            {fields.title && (
                <Typography as="h2" {...styles.tabTitle}>
                    {fields.title}
                </Typography>
            )}
            <TabGroup current={defaultTabState} onTabChange={handleTabChange} {...styles.tabGroup}>
                {allContent
                    .filter(t => (t.fields.faqBoxes?.length ?? 0) > 0) // hide empty tabs
                    .map((numberOfTab, index) => {
                    return (
                        <Tab
                            headline={numberOfTab.fields.name}
                            tabKey={numberOfTab.fields.name}
                            key={getTabAnchor(numberOfTab)}
                            {...styles.tabView}
                            id={getTabAnchor(numberOfTab)}
                            tabIndex={index}
                        >
                            <Accordion headingLevel={2}>
                                {numberOfTab.fields.faqBoxes.map((faqs: FAQModel, index: number) => {
                                    return (
                                        <StyledWrapper {...tabularDataStyles.accordianContainer} key={index}>
                                            <AccordionSection
                                                {...tabularDataStyles.accordianSection}
                                                title={faqs.fields.questions}
                                                expanded={index === expandedAccordian}
                                                onTogglePanel={() =>
                                                    index === expandedAccordian
                                                        ? setExpandedAccordian(null)
                                                        : setExpandedAccordian(index)
                                                }
                                            >
                                                <Typography>{parse(faqs.fields.answers, parserOptions)}</Typography>
                                            </AccordionSection>
                                        </StyledWrapper>
                                    );
                                })}
                            </Accordion>
                        </Tab>
                    );
                })}
            </TabGroup>
        </StyledWrapper>
    );
};

const SurTabAccordianWidget: WidgetModule = {
    component: connect(mapStateToProps)(SurTabAccordian),
    definition: {
        group: "Common",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.numberOfTabs,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.name;
                },
                defaultValue: [],
                fieldType: "General",
                sortOrder: 1,
                fieldDefinitions: [
                    {
                        name: "name",
                        displayName: "Tab Name",
                        editorTemplate: "TextField",
                        sortOrder: 0,
                        defaultValue: "",
                    },
                    {
                        name: "tabId",
                        displayName: "Tab ID",
                        editorTemplate: "TextField",
                        sortOrder: 0,
                        defaultValue: "",
                    },
                    {
                        name: fields.faqBoxes,
                        editorTemplate: "ListField",
                        getDisplay: (item: HasFields) => {
                            return item.fields.questions;
                        },
                        defaultValue: [],
                        sortOrder: 1,
                        fieldDefinitions: [
                            {
                                name: "questions",
                                displayName: "Questions",
                                editorTemplate: "TextField",
                                defaultValue: "",
                            },
                            {
                                name: "answers",
                                displayName: "Answers",
                                editorTemplate: "RichTextField",
                                defaultValue: "",
                            },
                        ],
                    },
                ],
            },
            {
                name: fields.cssClass,
                displayName: "Add CSS Class",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
        ],
    },
};
export default SurTabAccordianWidget;
